import { Box, Flex, Tag, TagCloseButton, Text } from "@chakra-ui/react";
import { t } from "i18next";
import { useCallback } from "react";
import { MinimalOrgDto } from "../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../common/errors/useApiError";
import { OrganizationSelector } from "../../common/organization/OrganizationSelector";
import { useSub } from "../../common/subscription/useSub";
import { useSaveOrganization } from "../organizations/current/useOrganizationEditor";
import { useAppDispatch } from "../../common/redux/hooks";
import { updateSelectedOrg } from "../../common/redux/reducers/authStateReducer";

export const InternalParties = () => {
  const authState = useLoggedInWithOrgContextState();
  const dispatch = useAppDispatch();
  const displayer = useApiError();
  const { canUpdateOrganization } = useSub();

  const { saveOrganization } = useSaveOrganization();

  const handleUpdatedOrganization = useCallback(
    async ({ organizations }: { organizations?: MinimalOrgDto[] }) => {
      const previousParties = authState.selectedOrg.internalParties;
      dispatch(updateSelectedOrg({ ...authState.selectedOrg, internalParties: organizations }));
      const parties = organizations?.filter((o) => o.type === "Organization");
      const selfRegisteredParties = organizations?.filter((o) => o.type === "OrganizationEntry");
      try {
        await saveOrganization({
          internalParties: {
            parties: parties?.map((o) => o.id),
            selfRegisteredParties: selfRegisteredParties?.map((o) => o.id),
          },
        });
      } catch (e) {
        dispatch(updateSelectedOrg({ ...authState.selectedOrg, internalParties: previousParties }));
        displayer.trigger(e);
      }
    },
    [authState.selectedOrg, dispatch, saveOrganization, displayer]
  );

  const selectedInternalParties = authState.selectedOrg.internalParties ?? [];
  const getOptionLabel = ({ organizationNumber, name }: MinimalOrgDto): string =>
    `${organizationNumber ? `${organizationNumber} - ` : ""}${name}`;
  const handleSelectOrganization = ({ organization }: { organization?: MinimalOrgDto }) => {
    if (!organization) return;
    handleUpdatedOrganization({ organizations: [...selectedInternalParties, organization] });
  };

  const removeOrg = (orgId: string) => {
    handleUpdatedOrganization({ organizations: selectedInternalParties.filter((o) => o.id !== orgId) });
  };

  return (
    <Box>
      <Box border="1px solid" borderColor="smBorder" rounded="md" p="3" bg="smBackground">
        {canUpdateOrganization && (
          <OrganizationSelector
            size="md"
            isClearable
            orgsToExclude={selectedInternalParties.map((o) => o.id)}
            onChange={handleSelectOrganization}
          />
        )}
        <Text fontSize="sm" color="smMuted" pt={canUpdateOrganization ? "2" : "0"} pb="2">
          {t("Selected internal parties")}
        </Text>
        <Flex flexDirection="column" gap="2">
          {selectedInternalParties.map((o) => (
            <Tag key={o.id} size="lg" variant="outline" color="smText">
              <Text mr="2">{getOptionLabel(o)}</Text>
              {canUpdateOrganization && (
                <TagCloseButton aria-label={`${t("Remove")} ${o.name}`} onClick={() => removeOrg(o.id)} />
              )}
            </Tag>
          ))}
          {selectedInternalParties.length === 0 && (
            <Text fontSize="sm" color="smMuted">
              {t("No internal parties selected")}
            </Text>
          )}
        </Flex>
      </Box>
    </Box>
  );
};
